/**
 * @fileoverview Professional Excel Export Utility Module
 *
 * This module provides enterprise-grade Excel export functionality with support for:
 * - Summary sections with metric boxes at the top of the sheet
 * - Formatted data tables with proper column headers
 * - Custom styling and cell formatting
 * - Timezone-aware date formatting
 *
 * @module helper/excelExport
 * @requires xlsx
 * @requires file-saver
 *
 * @example
 * // Basic usage for broadcast report export
 * import { exportBroadcastReport } from './helper/excelExport';
 *
 * const result = await exportBroadcastReport({
 *   contacts: contactsArray,
 *   counts: { total: 100, delivered: 90, opened: 50, lead: 20, clicked: 30, bounced: 5 },
 *   fileName: 'My_Broadcast_Report'
 * });
 *
 * @author GainWix Development Team
 * @version 1.0.0
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Status code to human-readable label mapping
 * Used for converting API status codes to user-friendly display text
 *
 * @constant {Object.<string, string>}
 */
const STATUS_LABELS = Object.freeze({
  BOUNCED: 'Bounced',
  SENDING: 'Sending',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  READ: 'Opened',
  REPLIED: 'Lead',
  CLICKED: 'Clicked',
  CREDIT_BLOCKED: 'Skipped',
});

/**
 * Payment status code to label mapping
 * Maps numeric payment status codes to descriptive text
 *
 * @constant {Object.<number, string>}
 */
const PAYMENT_STATUS_LABELS = Object.freeze({
  10: 'Completed',
  5: 'Pending',
  [-1]: 'Failed',
});

/**
 * Summary metric configuration for Excel export header section
 * Defines the order, keys, and display labels for summary boxes
 *
 * @constant {Array.<{key: string, label: string}>}
 */
const SUMMARY_METRICS = Object.freeze([
  { key: 'total', label: 'Total Contacts' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'lead', label: 'Lead' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'bounced', label: 'Bounced' },
  { key: 'skipped', label: 'Skipped' },
]);

/**
 * Default timezone for date formatting
 * Uses Indian Standard Time as per application requirements
 *
 * @constant {string}
 */
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Default locale for date/number formatting
 *
 * @constant {string}
 */
const DEFAULT_LOCALE = 'en-IN';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts a message status code to a human-readable label
 *
 * @function getStatusLabel
 * @param {string} status - The status code from the API (e.g., 'DELIVERED', 'READ')
 * @returns {string} Human-readable status label
 *
 * @example
 * getStatusLabel('READ'); // Returns 'Opened'
 * getStatusLabel('REPLIED'); // Returns 'Lead'
 */
const getStatusLabel = (status) => STATUS_LABELS[status] || 'Unknown';

/**
 * Converts a payment status code to a human-readable label
 *
 * @function getPaymentStatusLabel
 * @param {number} status - The numeric payment status code
 * @returns {string} Human-readable payment status label
 *
 * @example
 * getPaymentStatusLabel(10); // Returns 'Completed'
 * getPaymentStatusLabel(5); // Returns 'Pending'
 */
const getPaymentStatusLabel = (status) =>
  PAYMENT_STATUS_LABELS[status] || 'Unknown';

/**
 * Formats a date string or timestamp to localized date-time string
 * Uses Indian Standard Time by default
 *
 * @function formatDateTime
 * @param {string|number|Date} dateValue - The date value to format
 * @param {string} [timezone=DEFAULT_TIMEZONE] - The timezone for formatting
 * @param {string} [locale=DEFAULT_LOCALE] - The locale for formatting
 * @returns {string} Formatted date-time string or '-' if invalid
 *
 * @example
 * formatDateTime('2024-01-15T10:30:00Z');
 * // Returns '15 Jan 2024, 4:00:00 pm' (in IST)
 */
const formatDateTime = (
  dateValue,
  timezone = DEFAULT_TIMEZONE,
  locale = DEFAULT_LOCALE,
) => {
  if (!dateValue) return '-';

  try {
    return new Date(dateValue).toLocaleString(locale, {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  } catch {
    return '-';
  }
};

/**
 * Converts a column index to Excel column letter(s)
 * Handles multi-letter columns (AA, AB, etc.)
 *
 * @function getColumnLetter
 * @param {number} colIndex - Zero-based column index
 * @returns {string} Excel column letter(s)
 *
 * @example
 * getColumnLetter(0); // Returns 'A'
 * getColumnLetter(25); // Returns 'Z'
 * getColumnLetter(26); // Returns 'AA'
 */
const getColumnLetter = (colIndex) => {
  let letter = '';
  let temp = colIndex;

  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }

  return letter;
};

/**
 * Creates a cell reference string from row and column indices
 *
 * @function getCellRef
 * @param {number} row - Zero-based row index
 * @param {number} col - Zero-based column index
 * @returns {string} Excel cell reference (e.g., 'A1', 'B2')
 */
const getCellRef = (row, col) => `${getColumnLetter(col)}${row + 1}`;

// ============================================================================
// DATA TRANSFORMATION FUNCTIONS
// ============================================================================

/**
 * Transforms raw contact data from API to formatted export structure
 * Conditionally includes payment-related fields if present
 *
 * @function transformContactsForExport
 * @param {Array.<Object>} contacts - Raw contact data from API
 * @returns {Array.<Object>} Formatted contact objects ready for Excel export
 *
 * @example
 * const formattedData = transformContactsForExport(rawContacts);
 * // Returns array of objects with formatted keys like 'From Number', 'Status', etc.
 */
const transformContactsForExport = (contacts) => {
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return [];
  }

  return contacts.map((contact) => {
    // Build base contact object with required fields
    const baseContact = {
      'From Number': contact.from_number || '',
      'Country Code': contact.phone_country_code || '',
      'To Number': contact.mobile_number || '',
      'First Name': contact.first_name || '',
      'Last Name': contact.last_name || '',
      Status: getStatusLabel(contact.status),
      'Campaign Type': contact.campaign_type || '',
      'Send At': formatDateTime(contact.send_at),
    };

    // Conditionally add payment-related fields if they exist
    // This keeps the Excel columns clean when payment data isn't applicable
    const paymentFields = {};

    if (contact.reference_id) {
      paymentFields['Order ID'] = contact.reference_id;
    }

    if (typeof contact.payment_status !== 'undefined') {
      paymentFields['Payment Status'] = getPaymentStatusLabel(
        contact.payment_status,
      );
    }

    if (contact.total_amount) {
      paymentFields['Payment Amount'] = contact.total_amount;
    }

    return { ...baseContact, ...paymentFields };
  });
};

// ============================================================================
// EXCEL GENERATION FUNCTIONS
// ============================================================================

/**
 * Creates summary section data for the Excel worksheet
 * Generates a header row with metric labels and a data row with counts
 *
 * @function createSummarySection
 * @param {Object} counts - Object containing metric counts
 * @param {number} [counts.total=0] - Total contacts count
 * @param {number} [counts.delivered=0] - Delivered count
 * @param {number} [counts.opened=0] - Opened count
 * @param {number} [counts.lead=0] - Lead count
 * @param {number} [counts.clicked=0] - Clicked count
 * @returns {Array.<Array>} 2D array representing summary rows
 *
 * @example
 * const summary = createSummarySection({ total: 100, delivered: 90 });
 * // Returns [['Total Contacts', 'Delivered', ...], [100, 90, ...]]
 */
const createSummarySection = (counts = {}) => {
  // Header row with metric labels
  const headerRow = SUMMARY_METRICS.map((metric) => metric.label);

  // Data row with corresponding counts (default to 0 if not provided)
  const dataRow = SUMMARY_METRICS.map((metric) => counts[metric.key] ?? 0);

  return [headerRow, dataRow];
};

/**
 * Applies styling to the summary section cells
 * Note: XLSX community edition has limited styling support
 * This function sets up cell properties for potential styling
 *
 * @function applySummaryStyles
 * @param {Object} worksheet - XLSX worksheet object
 * @param {number} summaryRowCount - Number of rows in summary section
 * @param {number} columnCount - Number of columns to style
 */
const applySummaryStyles = (worksheet, summaryRowCount, columnCount) => {
  // Set column widths for better readability
  const colWidths = [];
  for (let i = 0; i < columnCount; i++) {
    colWidths.push({ wch: 18 }); // 18 characters width
  }
  worksheet['!cols'] = colWidths;
};

/**
 * Generates a complete Excel workbook with summary and data sections
 *
 * @function generateWorkbook
 * @param {Object} options - Generation options
 * @param {Array.<Object>} options.formattedContacts - Transformed contact data
 * @param {Object} options.counts - Summary counts object
 * @param {string} [options.sheetName='Broadcast Report'] - Name of the worksheet
 * @returns {Object} XLSX workbook object
 *
 * @example
 * const workbook = generateWorkbook({
 *   formattedContacts: transformedData,
 *   counts: { total: 100, delivered: 90 }
 * });
 */
const generateWorkbook = ({
  formattedContacts,
  counts,
  sheetName = 'Broadcast Report',
}) => {
  // Create new workbook
  const workbook = XLSX.utils.book_new();

  // COMMENTED OUT: Generate summary section (2 rows: header + data)
  // const summaryData = createSummarySection(counts);

  // COMMENTED OUT: Add empty row as separator between summary and table
  // const separatorRow = [[]];

  // Create headers for data table from first contact's keys
  const tableHeaders =
    formattedContacts.length > 0 ? Object.keys(formattedContacts[0]) : [];

  // Create table header row
  const tableHeaderRow = [tableHeaders];

  // Create table data rows
  const tableDataRows = formattedContacts.map((contact) =>
    tableHeaders.map((header) => contact[header] ?? ''),
  );

  // COMMENTED OUT: Combine all sections into single array of arrays
  // Structure: Summary Header | Summary Data | Empty Row | Table Header | Table Data
  // const allData = [
  //   ...summaryData, // Summary header + data (2 rows)
  //   [], // Empty separator row
  //   tableHeaders, // Data table header row
  //   ...tableDataRows, // Data table rows
  // ];

  // NEW: Only include table headers and data rows (no summary section)
  const allData = [
    tableHeaders, // Data table header row
    ...tableDataRows, // Data table rows
  ];

  // Convert to worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(allData);

  // Apply column styling
  const maxColumns = Math.max(
    SUMMARY_METRICS.length,
    tableHeaders.length,
  );
  applySummaryStyles(worksheet, 2, maxColumns);

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  return workbook;
};

/**
 * Converts workbook to binary array buffer for file saving
 *
 * @function workbookToArrayBuffer
 * @param {Object} workbook - XLSX workbook object
 * @returns {ArrayBuffer} Binary representation of the workbook
 */
const workbookToArrayBuffer = (workbook) => {
  return XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
};

// ============================================================================
// MAIN EXPORT FUNCTIONS
// ============================================================================

/**
 * Exports broadcast contact data to an Excel file with summary section
 *
 * This is the primary function for generating broadcast reports. It:
 * 1. Validates input data
 * 2. Transforms contacts to export format
 * 3. Creates summary section with metric boxes
 * 4. Generates and downloads the Excel file
 *
 * @async
 * @function exportBroadcastReport
 * @param {Object} options - Export configuration options
 * @param {Array.<Object>} options.contacts - Raw contact data from API
 * @param {Object} options.counts - Summary counts object
 * @param {number} options.counts.total - Total contacts count
 * @param {number} options.counts.delivered - Delivered count
 * @param {number} options.counts.opened - Opened count
 * @param {number} options.counts.lead - Lead count
 * @param {number} options.counts.clicked - Clicked count
 * @param {number} options.counts.bounced - Bounced count
 * @param {string} options.fileName - Base name for the exported file (without extension)
 * @param {string} [options.sheetName='Broadcast Report'] - Name of the worksheet
 * @returns {Promise.<{success: boolean, message: string, fileName?: string}>}
 *          Result object indicating success/failure with details
 *
 * @throws {Error} If file generation fails unexpectedly
 *
 * @example
 * // Complete usage example
 * const result = await exportBroadcastReport({
 *   contacts: apiResponse.broadcast_contacts,
 *   counts: {
 *     total: apiResponse.total_count,
 *     delivered: apiResponse.delivered_count,
 *     opened: apiResponse.opened_count,
 *     lead: apiResponse.lead_count,
 *     clicked: apiResponse.clicked_count
 *   },
 *   fileName: 'Campaign_March_2024'
 * });
 *
 * if (result.success) {
 *   console.log('Downloaded:', result.fileName);
 * } else {
 *   console.error('Failed:', result.message);
 * }
 */
export const exportBroadcastReport = async ({
  contacts,
  counts,
  fileName,
  sheetName = 'Broadcast Report',
}) => {
  try {
    // Validate required parameters
    if (!fileName) {
      return {
        success: false,
        message: 'File name is required for export',
      };
    }

    // Handle empty contacts array gracefully
    if (!Array.isArray(contacts) || contacts.length === 0) {
      return {
        success: false,
        message: 'No contacts available to export',
      };
    }

    // Normalize counts object with default values
    const normalizedCounts = {
      total: counts?.total ?? 0,
      delivered: counts?.delivered ?? 0,
      opened: counts?.opened ?? 0,
      lead: counts?.lead ?? 0,
      clicked: counts?.clicked ?? 0,
      bounced: counts?.bounced ?? 0,
    };

    // Transform raw contact data to export format
    const formattedContacts = transformContactsForExport(contacts);

    // Generate Excel workbook with summary and data sections
    const workbook = generateWorkbook({
      formattedContacts,
      counts: normalizedCounts,
      sheetName,
    });

    // Convert workbook to binary buffer
    const excelBuffer = workbookToArrayBuffer(workbook);

    // Create blob and trigger download
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Sanitize filename and ensure proper extension
    const sanitizedFileName = fileName.replace(/[<>:"/\\|?*]/g, '_');
    const fullFileName = `${sanitizedFileName}_Report.xlsx`;

    // Trigger file download
    saveAs(blob, fullFileName);

    return {
      success: true,
      message: 'Report downloaded successfully',
      fileName: fullFileName,
    };
  } catch (error) {
    // Log error for debugging while returning user-friendly message
    console.error('[ExcelExport] Error generating broadcast report:', error);

    return {
      success: false,
      message: 'Failed to generate report. Please try again.',
    };
  }
};

/**
 * Exports generic data to Excel file
 * Simplified function for basic data exports without summary section
 *
 * @async
 * @function exportToExcel
 * @param {Object} options - Export configuration
 * @param {Array.<Object>} options.data - Data array to export
 * @param {string} options.fileName - Base file name
 * @param {string} [options.sheetName='Sheet1'] - Worksheet name
 * @param {Array.<{from: string, to: string}>} [options.columnMappings] - Column rename mappings
 * @returns {Promise.<{success: boolean, message: string}>} Result object
 *
 * @example
 * await exportToExcel({
 *   data: myDataArray,
 *   fileName: 'My_Export',
 *   columnMappings: [
 *     { from: 'firstName', to: 'First Name' },
 *     { from: 'lastName', to: 'Last Name' }
 *   ]
 * });
 */
export const exportToExcel = async ({
  data,
  fileName,
  sheetName = 'Sheet1',
  columnMappings = [],
}) => {
  try {
    // Validate input
    if (!Array.isArray(data) || data.length === 0) {
      return {
        success: false,
        message: 'No data available to export',
      };
    }

    if (!fileName) {
      return {
        success: false,
        message: 'File name is required',
      };
    }

    // Apply column mappings if provided
    let exportData = data;

    if (columnMappings.length > 0) {
      exportData = data.map((item) => {
        const mappedItem = {};

        columnMappings.forEach(({ from, to }) => {
          if (item.hasOwnProperty(from)) {
            mappedItem[to] = item[from];
          }
        });

        return mappedItem;
      });
    }

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Convert to binary and save
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const fullFileName = `${fileName}.xlsx`;
    saveAs(blob, fullFileName);

    return {
      success: true,
      message: 'Export completed successfully',
    };
  } catch (error) {
    console.error('[ExcelExport] Error exporting to Excel:', error);

    return {
      success: false,
      message: 'Export failed. Please try again.',
    };
  }
};

// ============================================================================
// NAMED EXPORTS FOR UTILITY FUNCTIONS
// (Available for use when custom formatting is needed)
// ============================================================================

export {
  getStatusLabel,
  getPaymentStatusLabel,
  formatDateTime,
  transformContactsForExport,
  createSummarySection,
  STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  SUMMARY_METRICS,
};
